import React, {
  createContext,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  Alert,
  Box,
  Button,
  Drawer,
  TextField,
  Typography,
} from "@mui/material";
import AgentMarkdownMessage from "../common/AgentMarkdownMessage";
import { useDeveloperGuideAgentChat } from "../../hooks/useDeveloperGuideAgentChat";
import type { DeveloperGuideCodeSample, DeveloperGuideSection } from "../../types";
import {
  parseCodeSampleEnhancements,
  parseSectionEnhancements,
} from "../../utils/developerGuideAgentOutput";

interface EnhanceSectionRequest {
  guideName: string;
  section: DeveloperGuideSection;
  linkedApiUuids: string[];
  onAccept: (updated: Partial<DeveloperGuideSection>) => void;
}

interface GenerateSamplesRequest {
  guideName: string;
  section: DeveloperGuideSection;
  linkedApiUuids: string[];
  onAccept: (samples: DeveloperGuideCodeSample[]) => void;
}

export interface EnhanceWithAiContextValue {
  openEnhanceSection: (request: EnhanceSectionRequest) => void;
  openGenerateSamples: (request: GenerateSamplesRequest) => void;
}

export const EnhanceWithAiContext =
  createContext<EnhanceWithAiContextValue | null>(null);

interface EnhanceWithAiProviderProps {
  tenantId: string | null;
  children: ReactNode;
}

export const EnhanceWithAiProvider: React.FC<EnhanceWithAiProviderProps> = ({
  tenantId,
  children,
}) => {
  const { loading, error, sendMessage } = useDeveloperGuideAgentChat(tenantId);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [previewInstructions, setPreviewInstructions] = useState<string | null>(
    null,
  );
  const [previewTitle, setPreviewTitle] = useState<string | null>(null);
  const [previewSamples, setPreviewSamples] = useState<
    DeveloperGuideCodeSample[] | null
  >(null);
  const [pendingSection, setPendingSection] =
    useState<EnhanceSectionRequest | null>(null);
  const [pendingSamples, setPendingSamples] =
    useState<GenerateSamplesRequest | null>(null);

  const close = () => {
    setOpen(false);
    setPreviewInstructions(null);
    setPreviewTitle(null);
    setPreviewSamples(null);
    setPendingSection(null);
    setPendingSamples(null);
    setPrompt("");
  };

  const openEnhanceSection = useCallback((request: EnhanceSectionRequest) => {
    setPendingSection(request);
    setPendingSamples(null);
    setTitle(`Enhance: ${request.section.title || "Section"}`);
    setPrompt(
      `Enhance the instructions for section "${request.section.title}" in developer guide "${request.guideName}". ` +
        `Linked API UUIDs: ${request.linkedApiUuids.join(", ") || "none"}. ` +
        `Current instructions:\n${request.section.instructions}`,
    );
    setPreviewInstructions(null);
    setPreviewTitle(null);
    setPreviewSamples(null);
    setOpen(true);
  }, []);

  const openGenerateSamples = useCallback((request: GenerateSamplesRequest) => {
    setPendingSamples(request);
    setPendingSection(null);
    setTitle(`Generate samples: ${request.section.title || "Section"}`);
    setPrompt(
      `Generate code samples for section "${request.section.title}" in guide "${request.guideName}". ` +
        `Use linked API specs. API UUIDs: ${request.linkedApiUuids.join(", ") || "none"}. ` +
        `Return code-samples JSON for sectionId "${request.section.id}".`,
    );
    setPreviewInstructions(null);
    setPreviewTitle(null);
    setPreviewSamples(null);
    setOpen(true);
  }, []);

  const contextValue = useMemo(
    () => ({ openEnhanceSection, openGenerateSamples }),
    [openEnhanceSection, openGenerateSamples],
  );

  const handleSend = async () => {
    const response = await sendMessage(prompt);
    if (pendingSection) {
      const enhancements = parseSectionEnhancements(response);
      const match =
        enhancements.find((e) => e.sectionId === pendingSection.section.id) ??
        enhancements[0];
      if (match) {
        setPreviewTitle(match.title ?? pendingSection.section.title);
        setPreviewInstructions(
          match.instructions ?? pendingSection.section.instructions,
        );
      }
    }
    if (pendingSamples) {
      setPreviewSamples(parseCodeSampleEnhancements(response));
    }
  };

  const handleAccept = () => {
    if (pendingSection && previewInstructions != null) {
      pendingSection.onAccept({
        title: previewTitle ?? pendingSection.section.title,
        instructions: previewInstructions,
      });
    }
    if (pendingSamples && previewSamples) {
      pendingSamples.onAccept(previewSamples);
    }
    close();
  };

  return (
    <EnhanceWithAiContext.Provider value={contextValue}>
      {children}
      <Drawer
        anchor="right"
        open={open}
        onClose={close}
        PaperProps={{ sx: { width: 420, p: 2 } }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          {title}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={6}
          label="Prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          margin="normal"
        />
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
          <Button variant="contained" onClick={handleSend} disabled={loading}>
            {loading ? "Thinking..." : "Send to AI"}
          </Button>
          <Button onClick={close}>Cancel</Button>
        </Box>

        {(previewInstructions != null || previewSamples) && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Preview
            </Typography>
            {previewTitle && (
              <Typography variant="body2" fontWeight={600}>
                {previewTitle}
              </Typography>
            )}
            {previewInstructions != null && (
              <Box sx={{ mt: 1 }}>
                <AgentMarkdownMessage content={previewInstructions} />
              </Box>
            )}
            {previewSamples?.map((sample) => (
              <Box key={sample.id} sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {sample.label} ({sample.language})
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    bgcolor: "grey.100",
                    p: 1,
                    borderRadius: 1,
                    overflow: "auto",
                    fontSize: "0.8rem",
                  }}
                >
                  {sample.code}
                </Box>
              </Box>
            ))}
            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              <Button variant="contained" color="success" onClick={handleAccept}>
                Accept
              </Button>
              <Button
                onClick={() => {
                  setPreviewInstructions(null);
                  setPreviewTitle(null);
                  setPreviewSamples(null);
                }}
              >
                Discard
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>
    </EnhanceWithAiContext.Provider>
  );
};
